/**
 * SPRUCE WATER ENHANCERS - PRODUCT PAGE SCRIPTS
 * Handles all interactive functionality for the product page
 */

// Prevent re-declaration if script loads multiple times
if (typeof SpruceProductPage === 'undefined') {

  class SpruceProductPage {
    constructor() {
      this.init();
    }

    init() {
      // Initialize all components
      this.initNutritionModal();
      this.initVariantSelector();
      this.initQuantityControls();
      this.initFAQAccordion();
      this.initIngredientsCarousel();
      this.initTimelineAnimation(); // Active - Scroll linked rain/fill
      this.initStatsAnimation();
      this.initSubscriptionToggle();
      this.initPackSelector();
      this.initUpsells();
      this.initAppstleIntegration();
      // this.initRainEffect(); // Removed - simplified timeline
    }

    /**
     * Nutrition Modal
     */
    initNutritionModal() {
      const modalTriggers = document.querySelectorAll('[data-nutrition-modal-trigger]');
      const modal = document.getElementById('spruce-nutrition-modal');
      const closeButtons = document.querySelectorAll('[data-nutrition-modal-close]');
      const tabButtons = document.querySelectorAll('[data-nutrition-tab]');

      if (!modal) return;

      // Open modal
      modalTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          modal.classList.remove('spruce-hidden');
          document.body.style.overflow = 'hidden';
        });
      });

      // Close modal
      const closeModal = () => {
        modal.classList.add('spruce-hidden');
        document.body.style.overflow = '';
      };

      closeButtons.forEach(btn => {
        btn.addEventListener('click', closeModal);
      });

      // Close on overlay click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          closeModal();
        }
      });

      // Tab switching
      tabButtons.forEach(button => {
        button.addEventListener('click', () => {
          const flavor = button.dataset.nutritionTab;

          // Update active tab
          tabButtons.forEach(btn => btn.classList.remove('active'));
          button.classList.add('active');

          // Show corresponding content (if you have different nutrition per flavor)
          // This is a placeholder - adjust based on your actual implementation
          console.log('Switched to flavor:', flavor);
        });
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.classList.contains('spruce-hidden')) {
          closeModal();
        }
      });
    }

    /**
     * Variant Selector
     * Handles flavor selection and updates product images
     */
    initVariantSelector() {
      const variantButtons = document.querySelectorAll('[data-variant-id]');
      const mainImage = document.querySelector('[data-product-main-image]');
      const priceElement = document.querySelector('[data-product-price]');
      const variantIdInput = document.querySelector('[name="id"]');

      // Read swatch border colours from CSS custom properties (set by Liquid in :root)
      const getSwatchColour = (prop, fallback) => {
        const val = getComputedStyle(document.documentElement).getPropertyValue(prop).trim();
        return val || fallback;
      };

      variantButtons.forEach(button => {
        button.addEventListener('click', () => {
          const variantId = button.dataset.variantId;
          const variantImage = button.dataset.variantImage;
          const variantPrice = button.dataset.variantPrice;

          // Update active state + swatch circle border colour
          const activeColour   = getSwatchColour('--spruce-swatch-border-active',   '#059669');
          const inactiveColour = getSwatchColour('--spruce-swatch-border-inactive',  '#e2e8f0');

          variantButtons.forEach(btn => {
            btn.classList.remove('active');
            const circle = btn.querySelector('div[style*="border-radius: 9999px"]');
            if (circle) circle.style.borderColor = inactiveColour;
          });
          button.classList.add('active');
          const activeCircle = button.querySelector('div[style*="border-radius: 9999px"]');
          if (activeCircle) activeCircle.style.borderColor = activeColour;

          // Update main image
          if (mainImage && variantImage) {
            mainImage.src = variantImage;
            mainImage.srcset = variantImage;
          }

          // Update price if needed
          if (priceElement && variantPrice) {
            priceElement.textContent = variantPrice;
          }

          // Update hidden variant input for cart
          if (variantIdInput) {
            variantIdInput.value = variantId;
          }

          // Trigger Shopify variant change event
          const event = new CustomEvent('variant:change', {
            detail: { variantId, variantImage, variantPrice }
          });
          document.dispatchEvent(event);
        });
      });
    }

    /**
     * Quantity Controls
     */
    initQuantityControls() {
      const decreaseBtn = document.querySelector('[data-quantity-decrease]');
      const increaseBtn = document.querySelector('[data-quantity-increase]');
      const quantityInput = document.querySelector('[data-quantity-input]');
      const quantityDisplay = document.querySelector('[data-quantity-display]');

      if (!quantityInput) return;

      const updateQuantity = (newQuantity) => {
        const qty = Math.max(1, parseInt(newQuantity) || 1);
        quantityInput.value = qty;
        if (quantityDisplay) {
          quantityDisplay.textContent = qty;
        }
        this.updateTotalPrice();
      };

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          updateQuantity(parseInt(quantityInput.value) - 1);
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          updateQuantity(parseInt(quantityInput.value) + 1);
        });
      }

      // Allow direct input
      if (quantityDisplay) {
        quantityDisplay.addEventListener('blur', (e) => {
          updateQuantity(e.target.textContent);
        });
      }
    }

    /**
     * Update Total Price
     */
    updateTotalPrice() {
      const quantityInput = document.querySelector('[data-quantity-input]');
      const priceElement = document.querySelector('[data-product-price]');
      const totalPriceElement = document.querySelector('[data-total-price]');

      if (!quantityInput || !priceElement || !totalPriceElement) return;

      const quantity = parseInt(quantityInput.value) || 1;
      const price = parseFloat(priceElement.dataset.priceValue) || 0;
      const total = (quantity * price).toFixed(2);

      totalPriceElement.textContent = `£${total}`;
    }

    /**
     * FAQ Accordion
     */
    initFAQAccordion() {
      const faqButtons = document.querySelectorAll('[data-faq-toggle]');

      faqButtons.forEach(button => {
        button.addEventListener('click', () => {
          const faqId = button.dataset.faqToggle;
          const answer = document.getElementById(`faq-answer-${faqId}`);
          const icon = button.querySelector('[data-faq-icon]');

          if (!answer) return;

          // Toggle visibility
          const isOpen = !answer.classList.contains('spruce-hidden');

          // Close all other FAQs
          document.querySelectorAll('[data-faq-answer]').forEach(ans => {
            ans.classList.add('spruce-hidden');
          });
          document.querySelectorAll('[data-faq-icon]').forEach(ic => {
            ic.innerHTML = '<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>';
          });

          // Toggle current FAQ
          if (!isOpen) {
            answer.classList.remove('spruce-hidden');
            if (icon) {
              icon.innerHTML = '<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" /></svg>';
            }
          }
        });
      });
    }

    /**
     * Ingredients Carousel
     */
    initIngredientsCarousel() {
      const prevBtn = document.querySelector('[data-ingredients-prev]');
      const nextBtn = document.querySelector('[data-ingredients-next]');
      const container = document.querySelector('[data-ingredients-container]');

      if (!container) return;

      const getScrollAmount = () => {
        const card = container.querySelector('.spruce-ingredient-card');
        if (!card) return 300;

        const style = window.getComputedStyle(container);
        const gap = parseFloat(style.gap) || parseFloat(style.columnGap) || 24; // Default to 1.5rem/24px
        return card.offsetWidth + gap;
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          container.scrollBy({
            left: -getScrollAmount(),
            behavior: 'smooth'
          });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          container.scrollBy({
            left: getScrollAmount(),
            behavior: 'smooth'
          });
        });
      }

      // Drag to scroll functionality
      let isDown = false;
      let startX;
      let scrollLeft;

      container.addEventListener('mousedown', (e) => {
        isDown = true;
        container.style.cursor = 'grabbing';
        container.style.userSelect = 'none';
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
      });

      container.addEventListener('mouseleave', () => {
        isDown = false;
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      });

      container.addEventListener('mouseup', () => {
        isDown = false;
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      });

      container.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX) * 2; // Scroll-fast multiplier
        container.scrollLeft = scrollLeft - walk;
      });

      // Initial cursor style
      container.style.cursor = 'grab';
    }

    initTimelineAnimation() {
      const timelineSection = document.querySelector('.spruce-timeline');
      if (!timelineSection) return;

      const fill = document.getElementById('spruce-timeline-fill');
      const drop = document.getElementById('spruce-timeline-drop'); // Now the Sachet
      const steps = document.querySelectorAll('.spruce-timeline-step');

      // If missing elements (e.g. mobile or not rendered), bail
      if (!fill || !drop) return;

      const updateTimeline = () => {
        const rect = timelineSection.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const sectionHeight = rect.height;

        // Start filling when the top of the timeline is near the middle of screen
        // Trigger point: 50% down the viewport
        const triggerPoint = viewportHeight * 0.5;

        // how far has the top of the section traveled past the trigger point?
        // offset > 0 means we are scrolling into the section
        let offset = triggerPoint - rect.top;

        // Calculate progress (0 to 1+)
        let progress = offset / sectionHeight;

        // Clamp (allow slight overscroll for completion visuals)
        progress = Math.max(0, Math.min(1.05, progress));
        const percentage = progress * 100;

        // Visual clamp for CSS (don't go beyond 100%)
        const visualPercent = Math.min(100, percentage);

        // Update Fill and Sachet Position
        fill.style.height = `${visualPercent}%`;
        drop.style.top = `${visualPercent}%`;

        // Sachet Rotation (Wobble Effect)
        // wobbles back and forth as it falls
        const rotate = Math.sin(progress * 15) * 5;
        drop.style.transform = `translate(-50%, -50%) rotate(${rotate}deg)`;

        // Highlight steps
        steps.forEach(step => {
          const stepIcon = step.querySelector('.spruce-timeline-icon');
          if (!stepIcon) return;

          const stepRect = stepIcon.getBoundingClientRect();
          const stepCenterY = stepRect.top + (stepRect.height / 2);

          // Current water level in viewport coordinates
          // rect.top is the top of container. + (progress * height) = pixel position relative to container top
          const waterLevelY = rect.top + (progress * sectionHeight);

          if (waterLevelY >= stepCenterY - 50) {
            step.classList.add('active'); // Use class for cleaner CSS handling
          } else {
            step.classList.remove('active');
          }
        });
      };

      // Run on scroll
      window.addEventListener('scroll', () => requestAnimationFrame(updateTimeline));
      // Run once
      updateTimeline();
    }

    /**
     * Stats Count Animation
     */
    initStatsAnimation() {
      const stats = document.querySelectorAll('[data-stat-animate] [data-stat-value]');
      if (stats.length === 0) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animateValue(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.5
      });

      stats.forEach(stat => observer.observe(stat));
    }

    animateValue(element) {
      const start = parseFloat(element.dataset.start);
      const end = parseFloat(element.dataset.target);
      const duration = parseInt(element.dataset.duration) || 2000;
      const range = end - start;
      let startTime;

      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);

        // Easing function: easeOutQuart
        const ease = 1 - Math.pow(1 - progress, 4);

        const current = start + (range * ease);
        element.textContent = Math.floor(current);

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          element.textContent = end; // Ensure final value is exact
        }
      };

      requestAnimationFrame(step);
    }

    /**
     * Appstle Bridge
     * Appstle manages its own widget (injected into the Dawn product form).
     * We move it into our placeholder div so it's visible in our section,
     * then on form submit we read Appstle's selected selling_plan and write
     * it into our hidden input.
     */
    initSubscriptionToggle() {
      const form = document.getElementById('spruce-product-form');
      const placeholder = document.querySelector('.appstle-widget-placeholder');
      if (!form) return;

      // ── Price observer: correct Appstle prices whenever it re-renders ─────
      // Defined here so relocateWidget (below) can call it on the moved widget.
      const setupPriceObserver = (widget) => {
        let updating = false;
        const observer = new MutationObserver(() => {
          if (updating) return;
          if (typeof window._spruceUpdateAppstlePrices !== 'function') return;
          updating = true;
          setTimeout(() => {
            window._spruceUpdateAppstlePrices();
            setTimeout(() => { updating = false; }, 250);
          }, 80);
        });
        observer.observe(widget, { subtree: true, characterData: true, childList: true });
        console.log('[Spruce] Price observer attached');
      };

      // ── Widget relocation — extracted so it can be re-run after bfcache restore ──
      const relocateWidget = () => {
        if (!placeholder) return;
        // Already in the right place
        if (placeholder.querySelector('.appstle_sub_widget')) return;

        let pollCount = 0;
        const poll = setInterval(() => {
          const widget = document.querySelector('.appstle_sub_widget');
          if (widget) {
            placeholder.appendChild(widget);
            clearInterval(poll);
            console.log('[Spruce] Appstle widget moved into section');
            setupPriceObserver(widget);
          }
          if (++pollCount > 50) clearInterval(poll); // give up after 10s
        }, 200);
      };

      // Run on initial load
      relocateWidget();

      // Re-run when returning via browser back (bfcache restore)
      // pageshow fires on every page show; e.persisted = true means bfcache
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          console.log('[Spruce] bfcache restore — re-checking Appstle widget position');
          // Small delay to allow Appstle to re-render after restore
          setTimeout(relocateWidget, 500);
        }
      });

      // ── On submit: read Appstle's selection → inject into our form ────────
      // Guard flag ensures listener is only added once even if this method re-runs
      if (!form.dataset.spruceBridgeAttached) {
        form.dataset.spruceBridgeAttached = '1';
        form.addEventListener('submit', () => {
          const spInput = document.getElementById('spruce-selling-plan-input');
          if (!spInput) return;
          const appstleSelected = document.querySelector('input[name="selling_plan"]:checked');
          spInput.value = appstleSelected ? (appstleSelected.value || '') : '';
          console.log('[Spruce] Appstle bridge — selling_plan:', spInput.value || '(OTP)');
        }, true);
      }
    }

    /**
     * Pack Selector
     * Updates per-serving prices, total price cards and the checkout discount indicator
     * whenever a different pack or purchase type is chosen.
     */
    initPackSelector() {
      const selector = document.getElementById('spruce-pack-selector');
      if (!selector) return;

      const packBtns       = selector.querySelectorAll('.spruce-pack-btn');
      const discountEl     = document.getElementById('spruce-checkout-discount');
      const subPriceEl     = document.querySelector('[data-sub-price]');
      const otpStrikeEl    = document.querySelector('[data-otp-price]');
      const otpCardEl      = document.querySelector('[data-otp-card-price]');
      const subDiscountPct = parseFloat(selector.dataset.subDiscount || 0);

      const fmt = v => `£${parseFloat(v).toFixed(2)}`;

      // Read Appstle's current state — Subscribe = a plan ID is selected
      const getIsSubscribe = () => {
        const appstleSelected = document.querySelector('input[name="selling_plan"]:checked');
        return !!(appstleSelected && appstleSelected.value);
      };

      // Read active border / bg colours from section data attrs
      const getActiveStyles = () => {
        const card = document.querySelector('[data-active-border]');
        return {
          border: card ? card.dataset.activeBorder : '#059669',
          bg:     card ? card.dataset.activeBg     : '#ecfdf5',
        };
      };

      const updateAll = () => {
        const isSubscribe = getIsSubscribe();
        const activeBtn   = selector.querySelector('.spruce-pack-btn.active');

        // 1. Update per-serving text in every pack button
        packBtns.forEach(btn => {
          const el  = btn.querySelector('[data-per-serving]');
          if (!el) return;
          const val = isSubscribe ? btn.dataset.subPerServing : btn.dataset.otpPerServing;
          el.textContent = `${fmt(val)} / serving`;
        });

        // 2. Update price displays with active pack totals
        if (activeBtn) {
          const otpTotal = parseFloat(activeBtn.dataset.otpTotal || 0);
          const subTotal = parseFloat(activeBtn.dataset.subTotal || 0);

          if (subPriceEl)  subPriceEl.textContent  = fmt(subTotal);
          if (otpStrikeEl) otpStrikeEl.textContent = fmt(otpTotal);
          if (otpCardEl)   otpCardEl.textContent   = fmt(otpTotal);
        }

        // 3. Price-per-serving display below the pack grid
        if (discountEl && activeBtn) {
          const val = isSubscribe ? activeBtn.dataset.subPerServing : activeBtn.dataset.otpPerServing;
          discountEl.textContent = val ? `${fmt(val)} per serving` : '';
        }
      };

      // Wire up pack button clicks
      const { border: activeBorder, bg: activeBg } = getActiveStyles();
      packBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          packBtns.forEach(b => {
            b.classList.remove('active');
            b.style.borderColor = 'var(--spruce-slate-200)';
            b.style.background  = '#ffffff';
          });
          btn.classList.add('active');
          btn.style.borderColor = activeBorder;
          btn.style.background  = activeBg;
          updateAll();
        });
      });

      // Re-run when Appstle's selling_plan radio changes (Subscribe ↔ OTP)
      document.addEventListener('change', (e) => {
        if (e.target.name === 'selling_plan') updateAll();
      });

      // Initialise
      updateAll();
    }


    /**
     * Upsell Add to Cart
     */
    initUpsells() {
      // Upsell add-to-cart is handled by the inline script in
      // product-spruce-main.liquid (with cart-drawer integration).
      // Nothing to initialise here.
    }

    /**
     * Appstle Integration
     * Handles benefits formatting and fixes selection state visual bugs
     */
    initAppstleIntegration() {
      // 1. Event Delegation for Selection State Enforcer
      // Ensures only the selected option looks active, fixing the "double highlight" bug
      document.addEventListener('click', (e) => {
        const option = e.target.closest('.appstle_subscription_wrapper_option');
        if (option) {
          // Allow Appstle's internal logic to run first (microtask or immediate)
          // Then force our visual cleanup
          setTimeout(() => {
            const allOptions = document.querySelectorAll('.appstle_subscription_wrapper_option');

            // Remove active classes from all siblings
            allOptions.forEach(opt => {
              if (opt !== option) {
                opt.classList.remove('appstle_selected_background', 'appstle-active-option');

                // Also unchecked the radio if it's visually inactive (optional safety)
                const radio = opt.querySelector('input[type="radio"]');
                if (radio) radio.checked = false;
              }
            });

            // Ensure clicked option is active
            option.classList.add('appstle_selected_background');
            const radio = option.querySelector('input[type="radio"]');
            if (radio) radio.checked = true;

            // Trigger style updates if needed
          }, 50);
        }
      });

      // 2. Observer for Dynamic Content (Benefits)
      const observer = new MutationObserver((mutations) => {
        let shouldProcess = false;
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            shouldProcess = true;
            break;
          }
        }

        if (shouldProcess) {
          const descriptions = document.querySelectorAll('.appstle_subscription_description_text:not(.spruce-benefits-formatted)');

          descriptions.forEach(description => {
            if (description.textContent.includes(',')) {
              const text = description.textContent.trim();
              const benefits = text.split(',').map(item => item.trim()).filter(item => item);

              if (benefits.length > 1) {
                description.innerHTML = ''; // Clear existing text

                benefits.forEach(benefit => {
                  const div = document.createElement('div');
                  div.textContent = benefit;
                  description.appendChild(div);
                });

                // Helper class to mark as processed/styled
                description.classList.add('spruce-benefits-formatted');
              }
            }
          });
        }
      });

      // Start observing document body for widget injection
      observer.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * Timeline Rain Effect
     * Generates continuous falling water drops in the background
     */
    initRainEffect() {
      console.log('Spruce: initRainEffect starting...');
      const timelineInner = document.querySelector('.spruce-timeline');
      const timelineSection = timelineInner ? timelineInner.closest('.spruce-section') : null;

      if (!timelineSection) {
        console.warn('Spruce: Timeline section not found for rain effect');
        return;
      }

      // Only show on desktop
      if (window.innerWidth < 1024) {
        console.log('Spruce: Rain effect disabled on mobile');
        return;
      }

      console.log('Spruce: Timeline section found, adding rain effect...');

      // Create container for rain
      const container = document.createElement('div');
      container.style.cssText = `
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 5;
        overflow: hidden;
      `;
      timelineSection.style.position = 'relative';
      timelineSection.prepend(container);

      // Create a single raindrop
      function createRaindrop() {
        const drop = document.createElement('div');
        const x = Math.random() * 100;
        const size = 4 + Math.random() * 8;
        const duration = 2 + Math.random() * 2;

        drop.style.cssText = `
          position: absolute;
          left: ${x}%;
          top: -20px;
          width: ${size}px;
          height: ${size * 2.5}px;
          background: linear-gradient(to bottom, 
            transparent 0%, 
            rgba(100, 180, 255, 0.5) 30%, 
            rgba(60, 150, 255, 0.7) 100%);
          border-radius: 0 0 50% 50%;
          animation: spruce-rain-fall ${duration}s linear forwards;
          pointer-events: none;
        `;

        container.appendChild(drop);

        // Remove after animation
        setTimeout(() => drop.remove(), duration * 1000 + 100);
      }

      // Add the rain animation if not already present
      if (!document.getElementById('spruce-rain-animation')) {
        const style = document.createElement('style');
        style.id = 'spruce-rain-animation';
        style.textContent = `
          @keyframes spruce-rain-fall {
            0% {
              transform: translateY(0);
              opacity: 0;
            }
            10% {
              opacity: 0.8;
            }
            100% {
              transform: translateY(100vh);
              opacity: 0.3;
            }
          }
        `;
        document.head.appendChild(style);
      }

      // Create initial drops
      for (let i = 0; i < 20; i++) {
        setTimeout(() => createRaindrop(), i * 100);
      }

      // Continue creating drops
      setInterval(() => {
        createRaindrop();
      }, 100);

      console.log('Spruce: Rain effect initialized successfully!');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new SpruceProductPage();
    });
  } else {
    new SpruceProductPage();
  }

  // Expose for subscription app integration
  window.SpruceProductPage = SpruceProductPage;

} // End of re-declaration guard
